/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import BannerNotification from '@ziynx/components/notifications';
import Colors from '@ziynx/theme/colors';
import { Button } from '@ziynx/components/buttons';
import Link from '@ziynx/components/links';
import ActionTypes from '@ziynx/action-types';
import { useForm, Controller } from 'react-hook-form';
import { eventNames } from '@ziynx/analytics';
import { Heading, Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import Modal from '@ziynx/components/modals';
import Input from '@ziynx/components/inputs';
import Dropdown from '@ziynx/components/selects';
import { Form, FormRow } from '@ziynx/components/forms';
import { withAppManager, setupProfileImage, getInitials } from '@ziynx/components/utilities';
import { ImageAvatar } from '@ziynx/components/images';
import UploadPhotoModal from '@ziynx/pages/Modals/UploadPhoto';
import type { MongoIdType } from '@ziynx/flow-types/globals';
import type { ImageUploadConfig, RefinedCompanyVideos } from '@ziynx/flow-types/companyProfile';
import type { ContentType, MediaUploadReference } from '@ziynx/flow-types/media';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { INDUSTRIES, COMPANY_SIZE_OPTIONS } from '../../constants';

type Company = {
  _id?: MongoIdType,
  name: string,
  headline: string,
  industries: Array<{
    _id: string,
    name: string,
  }>,
  companySize: string,
  fetching: boolean,
  success: boolean,
  error: any,
  patching: boolean,
  patchSuccess: boolean,
  patchedImage: boolean,
  checking: boolean,
  videos: Array<RefinedCompanyVideos>,
  canEdit?: boolean,
  profileImage: {
    _id: MongoIdType,
    image: {
      uri: string,
      type: ContentType,
    },
    original: {
      uri: string,
      type: ContentType,
    }
  },
};

type Props = $ReadOnly<{|
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, params?: { from?: string, profileUpdated?: boolean }) => void,
    goBack: () => void,
    location: {
      state: {
        from: string,
      }
    }
  }
|}>;

const _modalWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '333px';
  case 'mobileSmall':
    return '240px';
  default:
    return '488px';
  }
};

const modalButtonWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '50%';
  case 'mobileSmall':
    return '100%';
  default:
    return '40%';
  }
};

const NOTIFICATION_MESSAGES = {
  incompleteFields: 'Please complete the required fields below to continue',
  saveSuccess: 'Changes saved successfully!',
  saveError: 'There was an issue saving your information.',
  saveStart: 'Saving...',
};

const PageContentWrapper = styled.div`
  padding: ${({ isMobile }) => (isMobile ? '40px 16px' : '40px')};
  box-sizing: border-box;
  max-width: 688px;
  width: 100%;
  ${({ isMobile }) => isMobile && 'margin: auto;'
}
`;

const StyledHeadingWrapper = styled.div`
  padding-bottom: 40px;
  width: max-content;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  margin-bottom: 24px;
`;

const LinkWrapper = styled.div`
  padding-bottom: 48px;
`;
const StyledBannerWrapper = styled.div`
  position: fixed;
  z-index:996;
  width: 100%;
  height: 0;
`;

const StyledButton = styled(Button)`
  ${({ lastChild }) => !lastChild && 'margin-right: 16px;'}
  ${({ marginBottom }) => marginBottom && 'margin-bottom: 16px'};
`;

const StyledBody = styled(Body)`
  padding-bottom: 12px;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 16px;
`;

const AvatarWrapper = styled.div`
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
`;

const ModalButton = styled(Button)`
  width: ${({ viewport }) => modalButtonWidth(viewport)};
  ${({ isMobileSmall, firstChild }) => !isMobileSmall && firstChild && 'margin-right: 16px;'}
  ${({ isMobileSmall, firstChild }) => isMobileSmall && firstChild && 'margin-bottom: 16px;'}
`;

const StyledModal = styled(Modal)`
  padding: 24px;
  height: 180px;
  width: ${({ viewport }) => _modalWidth(viewport)};
`;

const StyledFormRow = styled(FormRow)`
  ${({ top }) => top && 'margin-top: 40px;'};
`;

const INPUT_FIELDS = [
  'companyName',
  'headline',
  'industry',
  'companySize',
];

const EditProfileScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;

  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';
  const isMobileSmall = viewport === 'mobileSmall';

  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    setError,
    errors,
    reset,
    formState: {
      isDirty,
      isSubmitSuccessful,
      dirtyFields,
    },
  } = useForm();

  const [submittedData, setSubmittedData] = useState({});

  const [imageUploadConfig, setImageUploadConfig] = useState({});
  (imageUploadConfig: ImageUploadConfig);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...submittedData });
    }
  }, [isSubmitSuccessful, isDirty, reset]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_edit_company_profile,
      properties: {
        referrer: 'Profile Edit CTA',
      },
    });
  }, []);

  const companyService: Company = useSelector(({ company }) => {
    const group = company?.me ?? {};
    const profileImage = setupProfileImage(group?.profileImage);
    return {
      ...group,
      profileImage,
    };
  }, shallowEqual);

  const mediaService: MediaUploadReference = useSelector(
    ({ media }) => media?.companyProfileImage, shallowEqual,
  );

  // UPLOAD-PHOTO MODAL
  const [isModalOpen, setModalOpen] = useState(false);

  const [globalNotification, setGlobalNotification] = useState({
    visible: false,
    text: undefined,
    variant: 'error',
  });
  // NOTIFICATION MODAL
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    dispatch({
      type: ActionTypes.company.get.START,
      id: 'me',
    });
  }, []);

  useEffect(() => {
    if (companyService?.patchedImage) {
      dispatch({
        type: ActionTypes.company.reset,
        id: 'me',
      });
    }
  }, [companyService?.patchedImage]);

  useEffect(() => {
    if (!!companyService?.companySize && companyService?.success) {
      reset({
        companySize: companyService?.companySize,
      }, {
        dirtyFields: false,
      });
    }
  }, [companyService?.success]);

  const hideNotificationBanner = () => {
    setGlobalNotification({
      visible: false,
    });
  };
  const _setTimeout = () => setTimeout(hideNotificationBanner, 10000);

  useEffect(() => {
    if (companyService?.patchSuccess && !companyService?.patching) {
      history.push('/company-profile', { profileUpdated: true });
    } else if (companyService?.error) {
      hideNotificationBanner();
      setGlobalNotification({
        visible: true,
        variant: 'error',
        text: NOTIFICATION_MESSAGES.saveError,
      });
    }

    const timeout = _setTimeout();
    return () => {
      clearTimeout(timeout);
      hideNotificationBanner();
    };
  }, [companyService?.success]);

  useEffect(() => {
    if (mediaService?.uploadStatus === 'in-progress') {
      hideNotificationBanner();
      setGlobalNotification({
        visible: true,
        variant: 'success',
        text: NOTIFICATION_MESSAGES.saveStart,
      });
    } else if (mediaService?.uploadStatus === 'success' && mediaService?.fetching) {
      hideNotificationBanner();
      setGlobalNotification({
        visible: true,
        variant: 'success',
        text: NOTIFICATION_MESSAGES.saveSuccess,
      });
    } else if (mediaService?.uploadStatus === 'error') {
      hideNotificationBanner();
      setGlobalNotification({
        visible: true,
        variant: 'error',
        text: NOTIFICATION_MESSAGES.saveError,
      });
    }

    const timeout = _setTimeout();
    return () => {
      clearTimeout(timeout);
      hideNotificationBanner();
    };
  }, [mediaService?.uploadStatus]);

  const _openPhotoModal = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.opened_photo_modal,
    });
    setImageUploadConfig({
      destination: 'companyProfileImage',
      mediaUploadReferenceId: 'companyProfileImage',
      existingPhoto: {
        _id: companyService?.profileImage?._id,
        uri: companyService?.profileImage?.image?.uri,
        original: {
          uri: companyService?.profileImage?.original?.uri,
        },
      },
    });
    setModalOpen(true);
  };

  const _closePhotoModal = (): void => {
    setModalOpen(false);
  };

  const toggleModal = () => {
    setOpen(!isOpen);
  };

  const _getPatchOps = ({ data }) => {
    const ops = [];

    INPUT_FIELDS.forEach((field) => {
      if (!dirtyFields[field]) return;
      if (field === 'companyName' && data?.companyName) {
        ops.push(
          {
            op: 'replace',
            path: 'name',
            value: data.companyName,
          },
        );
      } else if (field === 'headline') {
        ops.push(data?.headline ? {
          op: 'replace',
          path: 'headline',
          value: data?.headline || '',
        } : {
          op: 'remove',
          path: 'headline',
        });
      } else if (field === 'industry' && data?.industry) {
        ops.push({
          op: 'replace',
          path: 'industries',
          value: [data?.industry],
        });
      } else if (field === 'companySize' && data?.companySize) {
        ops.push({
          op: 'replace',
          path: 'companySize',
          value: data?.companySize,
        });
      }
    });

    return ops;
  };

  const onSubmit = async (data: {
    companyName: string,
    headline: string,
    industry: string,
    companySize: string
  }) => {
    if ((!data.companyName)) {
      hideNotificationBanner();
      setGlobalNotification({
        visible: true,
        variant: 'error',
        text: NOTIFICATION_MESSAGES.incompleteFields,
      });
      setError('companyName', {
        type: 'manual',
        message: NOTIFICATION_MESSAGES.incompleteFields,
      });
      _setTimeout();
      return;
    }

    const patchOps = _getPatchOps({ data });

    setSubmittedData(data);

    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.saved_company_profile_changes,
    });

    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_edit_company_profile,
      properties: {
        referrer: 'Profile Edit CTA',
      },
    });

    dispatch({
      type: ActionTypes.company.patch.START,
      body: patchOps,
      id: 'me',
    });
  };

  const INITIALS = getInitials(companyService?.name);

  const _renderAvatar = (): React$Node => (
    <AvatarWrapper>
      <StyledBody medium>Profile Photo</StyledBody>
      <ImageAvatar
        avatar
        isLoading={mediaService?.fetching}
        placeholderText={INITIALS}
        overlayText={companyService?.canEdit ? 'Edit' : undefined}
        size="large"
        imageSource={companyService?.profileImage?.image?.uri}
        onClick={companyService?.canEdit ? _openPhotoModal : undefined}
      />
    </AvatarWrapper>
  );

  const [isSizeEmpty, setSizeEmpty] = useState(false);

  const [isIndustryEmpty, setIndustryEmpty] = useState(false);

  return (
    <>
      {globalNotification?.visible && (
        <StyledBannerWrapper>
          <BannerNotification
            animated
            showCloseIcon
            variant={globalNotification.variant}
            timeout={5000}
          >
            {globalNotification?.text}
          </BannerNotification>
        </StyledBannerWrapper>
      )}
      <Helmet>
        <title>{`${companyService?.name ?? ''} | Edit | ZIYNX`}</title>
      </Helmet>
      <ContentContainer isVertical={isMobile}>
        <UploadPhotoModal
          open={isModalOpen}
          imageUploadConfig={imageUploadConfig}
          onClose={_closePhotoModal}
        />
        <PageContentWrapper isMobile={isMobile}>
          <LinkWrapper>
            <Link
              variant="caret"
              color={Colors.GrayDark}
              onClick={(isDirty && !isSubmitSuccessful) ? toggleModal : () => {
                history.push('/company-profile', { from: 'edit-profile' });
              }}
            >
              Back to company profile

            </Link>
          </LinkWrapper>
          <StyledHeadingWrapper>
            <Heading large={!isMobile} medium={isMobile} color={Colors.GrayVeryDark}>
              Edit company details
            </Heading>
          </StyledHeadingWrapper>
          { _renderAvatar() }
          <Form
            formID="edit-profile"
          >
            <FormRow>
              <Controller
                control={control}
                render={({ onChange, value }) => (
                  <Input
                    variant="text"
                    inputName="companyName"
                    label="Company Name"
                    value={value}
                    required
                    disabled={!companyService?.canEdit || companyService?.fetching}
                    error={!!errors?.companyName}
                    onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    }}
                  />
                )}
                name="companyName"
                defaultValue={companyService?.name ?? ''}
              />
            </FormRow>
            <FormRow>
              <Controller
                control={control}
                render={({ onChange, value }) => (
                  <Input
                    variant="textarea"
                    inputName="headline"
                    label="Headline"
                    value={value}
                    disabled={!companyService?.canEdit || companyService?.fetching}
                    onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    }}
                  />
                )}
                name="headline"
                defaultValue={companyService?.headline ?? ''}
              />
            </FormRow>
            <StyledFormRow top paddingBottom="40px">
              <Controller
                control={control}
                render={({ onChange }) => (
                  <Dropdown
                    inputName="industry"
                    activeLabel="Industry"
                    label="Industry"
                    required
                    disabled={!companyService?.canEdit || companyService?.fetching}
                    options={INDUSTRIES}
                    defaultValue={(companyService?.industries ?? [])[0]?._id}
                    onChange={(event: string): void => {
                      if (event === '') setIndustryEmpty(true);
                      else setIndustryEmpty(false);
                      onChange(event);
                    }}
                  />
                )}
                name="industry"
                defaultValue={(companyService?.industries ?? [])[0]?._id ?? ''}
              />
            </StyledFormRow>
            <StyledFormRow paddingBottom="40px">
              <Controller
                control={control}
                render={({ onChange }) => (
                  <Dropdown
                    inputName="No. of employees"
                    activeLabel="No. of employees"
                    label="No. of employees"
                    required
                    error={!!errors?.companySize}
                    errorLabel={errors?.companySize?.message}
                    disabled={!companyService?.canEdit || companyService?.fetching}
                    options={COMPANY_SIZE_OPTIONS}
                    defaultValue={companyService?.companySize}
                    onChange={(event: string): void => {
                      if (event === '') setSizeEmpty(true);
                      else setSizeEmpty(false);
                      onChange(event);
                    }}
                  />
                )}
                name="companySize"
                defaultValue={companyService?.companySize}
                rules={{
                  required: 'No. of employees required',
                  validate: (value) => !!value.trim(),
                }}
              />
            </StyledFormRow>
            <ButtonWrapper isMobileSmall={isMobileSmall}>
              <StyledButton
                isLoading={companyService?.fetching}
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty || isSizeEmpty || isIndustryEmpty || mediaService?.fetching}
                type="submit"
                form="edit-profile"
                width="100%"
                marginBottom={isMobileSmall}
              >
                Save
              </StyledButton>
              <StyledButton
                onClick={(isDirty && !isSubmitSuccessful) ? toggleModal : () => {
                  history.goBack();
                }}
                type="submit"
                inverted
                form="edit-profile"
                lastChild={!isMobileSmall}
                width="100%"
              >
                Cancel
              </StyledButton>
            </ButtonWrapper>
          </Form>
        </PageContentWrapper>
        <StyledModal open={isOpen} viewport={viewport}>
          <ModalContent>
            <StyledHeading small>Are you sure you want to discard your changes?</StyledHeading>
            <ModalButtonWrapper isMobileSmall={isMobileSmall}>
              <ModalButton
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                firstChild
                onClick={() => {
                  history.goBack();
                }}
              >
                Back to Profile

              </ModalButton>
              <ModalButton
                inverted
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                onClick={toggleModal}
              >
                Cancel
              </ModalButton>
            </ModalButtonWrapper>
          </ModalContent>
        </StyledModal>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(EditProfileScreen): React$ComponentType<ManagedProps<Props>>);
